import React from 'react';
import { Controller } from 'react-hook-form';

import type { Control } from 'react-hook-form';
import type { IResourceClientMember, IResourceDatabaseField } from '@/api/types';
import type { IFormDataOptions } from '@/types/forms';
import type { IFileProxy } from './types';

import { ResourceDatabaseFieldTypeId } from '@/api/enums';
import { Input } from './Input';
import { formData } from '@/helpers';
import { Select } from './Select';
import { FileInput } from './FileInput';
import { ATTACHMENT_SIZE_LIMIT } from '@/config/variables';
import { Checkbox } from './Checkbox';
import { Textarea } from './Textarea';
import { DateTimeInput } from './DateTimeInput';

interface IDatabaseFieldInput {
  field: IResourceDatabaseField;
  formDataOptions: IFormDataOptions;
  control: Control<Record<string, string | number | IFileProxy[] | null>>;
  memberList: IResourceClientMember[];
}

const inputComponentsMap: {
  [key: number]: (props: IDatabaseFieldInput) => JSX.Element;
} = {
  [ResourceDatabaseFieldTypeId.Text]: function FieldTextInput({ field, formDataOptions }) {
    return (
      <Input
        name={field.columnName}
        type="text"
        label={field.name}
        required={field.required}
        description={field.description}
        {...formData(field.columnName, formDataOptions)}
      />
    );
  },
  [ResourceDatabaseFieldTypeId.TextMultiline]: function FieldTextMultilineInput({ field, formDataOptions, control }) {
    return (
      <Textarea
        name={field.columnName}
        label={field.name}
        control={control}
        required={field.required}
        description={field.description}
        {...formData(field.columnName, formDataOptions)}
      />
    );
  },
  [ResourceDatabaseFieldTypeId.Number]: function FieldNumberInput({ field, formDataOptions }) {
    return (
      <Input
        name={field.columnName}
        type="number"
        step="any"
        label={field.name}
        required={field.required}
        description={field.description}
        {...formData(field.columnName, formDataOptions)}
      />
    );
  },
  [ResourceDatabaseFieldTypeId.Checkbox]: function FieldCheckboxInput({ field, formDataOptions }) {
    return (
      <Checkbox
        name={field.columnName}
        label={field.name}
        required={field.required}
        description={field.description}
        isStandalone={false}
        {...formData(field.columnName, formDataOptions)}
      />
    );
  },
  [ResourceDatabaseFieldTypeId.Time]: function FieldTimeInput({ field, formDataOptions }) {
    return (
      <Input
        name={field.columnName}
        type="time"
        label={field.name}
        required={field.required}
        description={field.description}
        {...formData(field.columnName, formDataOptions)}
      />
    );
  },
  [ResourceDatabaseFieldTypeId.Date]: function FieldDateInput({ field, formDataOptions }) {
    return (
      <Input
        name={field.columnName}
        type="date"
        label={field.name}
        required={field.required}
        description={field.description}
        {...formData(field.columnName, formDataOptions)}
      />
    );
  },
  [ResourceDatabaseFieldTypeId.DateTime]: function FieldDateTimeInput({ field, formDataOptions }) {
    return (
      <DateTimeInput
        dateName={`${field.columnName}-date`}
        timeName={`${field.columnName}-time`}
        formDataOptions={formDataOptions}
        label={field.name}
        required={field.required}
        description={field.description}
      />
    );
  },
  [ResourceDatabaseFieldTypeId.Currency]: function FieldCurrencyInput({ field, formDataOptions }) {
    return (
      <Input
        name={field.columnName}
        type="number"
        step="0.01"
        label={field.name}
        required={field.required}
        description={field.description}
        {...formData(field.columnName, formDataOptions)}
      />
    );
  },
  [ResourceDatabaseFieldTypeId.Email]: function FieldEmailInput({ field, formDataOptions }) {
    return (
      <Input
        name={field.columnName}
        type="text"
        label={field.name}
        required={field.required}
        description={field.description}
        {...formData(field.columnName, formDataOptions)}
      />
    );
  },
  [ResourceDatabaseFieldTypeId.Phone]: function FieldPhoneInput({ field, formDataOptions }) {
    return (
      <Input
        name={field.columnName}
        type="text"
        label={field.name}
        required={field.required}
        description={field.description}
        {...formData(field.columnName, formDataOptions)}
      />
    );
  },
  [ResourceDatabaseFieldTypeId.WebLink]: function FieldWebLinkInput({ field, formDataOptions }) {
    return (
      <Input
        name={field.columnName}
        type="text"
        label={field.name}
        required={field.required}
        description={field.description}
        {...formData(field.columnName, formDataOptions)}
      />
    );
  },
  [ResourceDatabaseFieldTypeId.FileAttachment]: function FieldFileAttachmentInput({ field: tableField, control }) {
    return (
      <Controller
        name={tableField.columnName}
        control={control}
        render={({ field, fieldState }) => (
          <FileInput
            name={tableField.columnName}
            label={tableField.name}
            value={field.value as IFileProxy[]}
            error={fieldState.error}
            setValue={field.onChange}
            maxSize={ATTACHMENT_SIZE_LIMIT}
            maxFiles={1}
            description={tableField.description}
          />
        )}
      />
    );
  },
  [ResourceDatabaseFieldTypeId.List]: function FieldListInput({ field, formDataOptions }) {
    return (
      <Select
        name={field.columnName}
        label={field.name}
        required={field.required}
        options={field.properties.split(',').map(option => ({ label: option.trim(), value: option.trim() }))}
        description={field.description}
        {...formData(field.columnName, formDataOptions)}
      />
    );
  },
  [ResourceDatabaseFieldTypeId.MemberList]: function FieldMemberListInput({ field, formDataOptions, memberList }) {
    return (
      <Select
        name={field.columnName}
        label={field.name}
        required={field.required}
        options={memberList.map(option => ({ label: option.fullName, value: option.userId }))}
        description={field.description}
        {...formData(field.columnName, formDataOptions)}
      />
    );
  },
  [ResourceDatabaseFieldTypeId.Calculated]: function FieldCalculatedInput() {
    return <></>;
  },
};

const DatabaseFieldInput = (props: IDatabaseFieldInput): React.ReactElement => {
  const InputComponent = inputComponentsMap[props.field.rtxtype_Id];
  return <InputComponent {...props} />;
};

export { DatabaseFieldInput };
